import { useCallback, useMemo, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption, TriggerFn } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { ListItemButton } from '@mui/material';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { TextNode } from 'lexical';

import { $createMergeTagNode } from './utils';

const triggerFn: TriggerFn = (text) => {
  // Match `{{` followed by any identifier-like string at the end of the text
  const match = /\{\{([a-zA-Z0-9._]*)$/.exec(text);
  if (match) {
    return {
      leadOffset: text.length - match[0].length, // where `{{user.f` starts
      matchingString: match[1], // the part after `{{` → "user.f"
      replaceableString: match[0], // the full match → "{{user.f"
    };
  }

  return null;
};

// type MergeTagOption = {
//   name: string;
//   description: string;
// };

class MergeTagMenuOption extends MenuOption {
  constructor(
    public mergeTag: MergeTag,
    key: string,
  ) {
    super(key);
  }

  public getDescription() {
    // if the merge tag has an example value
    if (this.mergeTag.example) {
      return `${this.mergeTag.description} (e.g. ${this.mergeTag.example})`;
    }
  }
}

interface MergeTagPluginProps {
  mergeTags: MergeTags;
}

export default function MergeTagPlugin({ mergeTags }: MergeTagPluginProps) {
  const [queryString, setQueryString] = useState<string | null>(null);
  const [editor] = useLexicalComposerContext();

  const mergeTagList = useMemo(() => Object.values(mergeTags.map), [mergeTags.map]);

  const menuOptions = useMemo(
    () => mergeTagList.map((mergeTag) => new MergeTagMenuOption(mergeTag, mergeTag.name)),
    [mergeTagList],
  );

  // get results based on current query string
  const filteredMenuOptions = useMemo(() => {
    return menuOptions.filter(({ mergeTag }) =>
      mergeTag.name.toLowerCase().startsWith(queryString?.toLowerCase() ?? ''),
    );
  }, [menuOptions, queryString]);

  // const options = useMemo(() => results.map((result) => new MergeTagMenuOption(result, result.name)), [results]);

  // const triggerFn = useBasicTypeaheadTriggerMatch('{{', {
  //   minLength: 0,
  // });

  const onSelectOption = useCallback(
    (selectedOption: MergeTag, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mergeTagNode = $createMergeTagNode(selectedOption);
        if (nodeToReplace) {
          nodeToReplace.replace(mergeTagNode);
        }
        // mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MergeTagMenuOption>
      triggerFn={triggerFn}
      onQueryChange={setQueryString}
      onSelectOption={(option, nodeToReplace, match) => onSelectOption(option.mergeTag, nodeToReplace, match)}
      options={filteredMenuOptions}
      menuRenderFn={(anchorRef, { options, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!options.length || !anchorRef?.current) return null;

        const anchor = anchorRef.current.getBoundingClientRect();
        console.log(anchor);

        return (
          <Popper
            open
            anchorEl={anchorRef.current}
            placement="bottom-start"
            // disablePortal
            sx={{
              maxHeight: '50px',
              // minWidth: '300px',
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <Paper>
              <List dense>
                {options.map((option, i) => (
                  <ListItemButton
                    key={option.key}
                    selected={i === selectedIndex}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    onClick={() => selectOptionAndCleanUp(option)}
                  >
                    {option.getDescription()}
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Popper>
        );
      }}
    />
  );
}
